'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HeroPatternBackground } from "@/components/marketing/HeroPatternBackground";
import { MapPin, Clock, Users, Lightbulb, Heart, Eye, TrendingUp, CheckCircle2 } from 'lucide-react';const openPositions = [
  { id: 1, title: 'Senior Full Stack Developer',   department: 'Engineering',       location: 'Remote / San Francisco', type: 'Full-time', description: 'Join our engineering team to build the next generation of HR technology.',   color: 'hsl(var(--primary))' },
  { id: 2, title: 'Product Marketing Manager',     department: 'Marketing',         location: 'Remote / New York',      type: 'Full-time', description: 'Drive product marketing strategy and go-to-market execution.',              color: 'hsl(var(--success))' },
  { id: 3, title: 'Customer Success Manager',      department: 'Customer Success',  location: 'Remote',                 type: 'Full-time', description: 'Help our customers achieve success with Hirebee HRMS platform.',           color: 'hsl(var(--warning))' },
  { id: 4, title: 'HR Business Partner',           department: 'People',            location: 'Remote / Austin',        type: 'Full-time', description: 'Partner with leadership to drive people strategy and culture.',             color: 'hsl(var(--accent))'  },
];

const benefits = [
  'Competitive salary and equity',
  'Comprehensive health, dental, and vision insurance',
  'Flexible PTO and sabbatical programs',
  'Remote-first culture with home office stipend',
  'Professional development budget',
  '401(k) with company matching',
];

const values = [
  { title: 'Innovation First',  description: 'We push boundaries and embrace new technologies to solve complex HR challenges.',                              icon: Lightbulb  },
  { title: 'People-Centric',   description: 'We believe great products come from great teams and prioritize employee well-being.',                          icon: Heart      },
  { title: 'Transparency',     description: 'We operate with openness, honesty, and clear communication at all levels.',                                    icon: Eye        },
  { title: 'Growth Mindset',   description: 'We learn from failures, celebrate wins, and continuously improve ourselves and our product.',                  icon: TrendingUp },
];

export default function CareersClient() {
  const scrollToPositions = () => {
    document.getElementById('open-positions')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative pt-24 pb-16 overflow-hidden" style={{ backgroundColor: '#E9FBFA' }}>
        <HeroPatternBackground className="hidden md:block absolute inset-0 w-full h-full object-cover pointer-events-none opacity-60" priority />
        <div className="relative container mx-auto text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Build the Future of HR Technology
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Join our mission to revolutionize how companies manage their most valuable asset: their people. We're building tools that make HR teams more effective and employees more engaged.
            </p>
            <div>
              <Button size="lg" className="mb-4" onClick={scrollToPositions}>
                View Open Positions
              </Button>
              <p className="text-sm text-muted-foreground">Remote-first • Competitive packages • Growth opportunities</p>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-16">
        {/* Company Values */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value) => (
              <Card key={value.title} className="macos-card h-full hover:shadow-medium transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <value.icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle>{value.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Why Work at Hirebee?</h2>
              <p className="text-muted-foreground mb-6">
                We offer comprehensive benefits and a culture that supports your growth, well-being, and career development. Join a team that values innovation, collaboration, and making a meaningful impact.
              </p>
              <ul className="space-y-3">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="macos-card">
                <CardContent className="p-8">
                  <div className="text-center">
                    <div className="text-4xl mb-4">🚀</div>
                    <h3 className="font-semibold mb-2">Fast-Growing Team</h3>
                    <p className="text-muted-foreground mb-4">
                      Join us at an exciting stage of growth where your contributions directly shape the future of our product and company.
                    </p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-primary">50+</div>
                        <div className="text-xs text-muted-foreground">Employees</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-primary">15</div>
                        <div className="text-xs text-muted-foreground">Countries</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-primary">100%</div>
                        <div className="text-xs text-muted-foreground">Remote-first</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section id="open-positions">
          <h2 className="text-3xl font-bold text-center mb-12">Open Positions</h2>
          <div className="space-y-6">
            {openPositions.map((position) => (
              <Card key={position.id} className="macos-card hover:shadow-medium transition-all duration-300 hover:-translate-y-0.5 overflow-hidden">
                <div className="flex">
                  <div className="w-1 shrink-0" style={{ backgroundColor: position.color }} />
                  <CardContent className="p-6 flex-1">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold">{position.title}</h3>
                          <Badge variant="secondary">{position.department}</Badge>
                        </div>
                        <p className="text-muted-foreground mb-4">{position.description}</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {position.location}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {position.type}
                          </div>
                        </div>
                      </div>
                      <Button>Apply Now</Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>

          <Card className="mt-12 text-center overflow-hidden bg-gradient-to-br from-primary/5 via-background to-accent/5 border-primary/10">
            <CardContent className="p-8">
              <Users className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Don't See Your Role?</h3>
              <p className="text-muted-foreground mb-6">
                We're always looking for talented individuals to join our team. Send us your resume and tell us how you'd like to contribute.
              </p>
              <div className="flex justify-center gap-4">
                <Button>Send General Application</Button>
                <Button variant="outline">Join Our Talent Pool</Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </main>
  );
}
